// client/src/components/UploadedMedia.js
import React, { useState } from "react";
// No axios import needed here anymore!
import {
  Typography,
  ImageList,
  ImageListItem,
  CircularProgress,
  Card,
  CardContent,
  Box,
  Chip,
  Stack,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Import the custom hooks
import { useUploadedMedia } from "../../hooks/useUploadedMedia";
import { useLikes } from "../../hooks/useLikes";

// --- MediaGridItem Component ---
// This component manages individual media item rendering and its like status.
// It uses the useLikes hook.

// --- MediaGridItem Component ---
// This component manages individual media item rendering and its like status.
// It uses the useLikes hook.
const MediaGridItem = ({ media, likeCount, currentDisplaySize }) => {
  console.log("=== MEDIA GRID ITEM DEBUG ===");
  console.log("Media ID:", media._id);
  console.log("Current display size:", currentDisplaySize);
  console.log("Media mediaUrl object:", media.mediaUrl);
  console.log("Available sizes:", Object.keys(media.mediaUrl || {}));
  console.log(
    "Requested size available?",
    !!media.mediaUrl?.[currentDisplaySize]
  );
  console.log("=== END DEBUG ===");

  // Use the useLikes hook for individual item like management
  const { isLiked, toggleLike, likeLoading, likeError } = useLikes(media._id);

  // Helper function to get the best available image URL with fallback to smallest
  const getImageUrl = (mediaUrl, requestedSize) => {
    if (!mediaUrl || typeof mediaUrl !== 'object') {
      return null;
    }

    // Try requested size first
    if (mediaUrl[requestedSize]) {
      return mediaUrl[requestedSize];
    }

    // Get all available sizes and sort to find the smallest
    const availableSizes = Object.keys(mediaUrl);
    if (availableSizes.length === 0) {
      return null;
    }

    // Define size hierarchy (smallest to largest)
    const sizeHierarchy = ['small', 'medium', 'large'];
    
    // Find the smallest available size by checking hierarchy order
    const smallestSize = sizeHierarchy.find(size => availableSizes.includes(size));
    
    if (smallestSize) {
      console.log(`Falling back from '${requestedSize}' to smallest available '${smallestSize}' for media ${media._id}`);
      return mediaUrl[smallestSize];
    }

    // If none of the standard sizes exist, use the first available
    console.log(`Using first available size '${availableSizes[0]}' for media ${media._id}`);
    return mediaUrl[availableSizes[0]];
  };

  // Determine the URL for the image with fallback logic
  const imageUrl = getImageUrl(media.mediaUrl, currentDisplaySize);

  if (!imageUrl) {
    console.warn(
      `MediaGridItem: Media item ${media._id} has no valid imageUrl for any size.`
    );
    return (
      <ImageListItem key={media._id}>
        <img
          src="https://via.placeholder.com/150?text=Image+Not+Available"
          alt="Image not available"
          style={{
            borderRadius: 4,
            width: "100%",
            height: "auto",
            opacity: 0.7,
          }}
        />
      </ImageListItem>
    );
  }

  return (
    <ImageListItem key={media._id}>
      <Box position="relative">
        <img
          src={imageUrl}
          alt={media.description || "Uploaded Media"}
          loading="lazy"
          style={{
            borderRadius: 4,
            cursor: "pointer",
            width: "100%",
            height: "auto",
          }}
          onError={(e) => {
            // Prevent infinite loop by checking if we're already showing the fallback
            if (e.target.src.includes('via.placeholder.com')) {
              console.error(`MediaGridItem: Fallback image also failed for media ID: ${media._id}`);
              return;
            }
            
            e.target.onerror = null; // Prevent further error events
            e.target.src = "https://via.placeholder.com/150?text=Image+Not+Available";
            e.target.style.opacity = "0.7"; // Make failed images look consistent
            console.error(
              `MediaGridItem: Failed to load image for media ID: ${media._id}, URL: ${imageUrl}`
            );
          }}
        />

        {/* Like Button */}
        <IconButton
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
            padding: "4px",
            pointerEvents: likeLoading ? "none" : "auto", // Disable button when loading
          }}
          onClick={toggleLike}
          disabled={likeLoading}
          aria-label={isLiked ? "Unlike" : "Like"}
        >
          {isLiked ? (
            <FavoriteIcon sx={{ color: "#ff4081", fontSize: 20 }} />
          ) : (
            <FavoriteBorderIcon sx={{ color: "text.secondary", fontSize: 20 }} />
          )}
          {likeLoading && <CircularProgress size={16} sx={{ position: 'absolute' }} />}
        </IconButton>

        {/* Like Count Display */}
        {likeCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 4,
              right: 4,
              display: "flex",
              alignItems: "center",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              borderRadius: 8,
              padding: "2px 6px",
            }}
          >
            <FavoriteIcon sx={{ color: "#ff4081", fontSize: 16, mr: 0.5 }} />
            <Typography variant="caption" sx={{ color: "white", fontWeight: "bold" }}>
              {likeCount}
            </Typography>
          </Box>
        )}

        {/* Individual Like Error */}
        {likeError && (
          <Alert severity="error" sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 0.5, m: 0, fontSize: "0.7rem" }}>
            {likeError}
          </Alert>
        )}
      </Box>
    </ImageListItem>
  );
};


// --- UploadedMedia Component ---
// This is the main component that renders the uploaded media UI.
// It uses the useUploadedMedia hook to get data.
export default function UploadedMedia({ size = "small" }) {
  // Destructure values from the useUploadedMedia hook
  const {
    mediaByDivesite,
    mediaByDive,
    mediaLoading,
    totalMediaCount,
    divesiteCount,
    diveCount,
    likeCounts,
    mediaError,
    getClaimedImageSize,
  } = useUploadedMedia();

  const [tabValue, setTabValue] = useState(0);

  // Get the determined image size based on user claims
  const currentDisplaySize = getClaimedImageSize();

  const getImageGridCols = () => {
    switch (size) {
      case "small":
        return 2;
      case "medium":
        return 3;
      case "large":
        return 4;
      default:
        return 2;
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Helper function to render a grid of media items
  const renderMediaGrid = (mediaList) => {
    if (mediaList.length === 0) {
      return <Typography color="textSecondary">No images available.</Typography>;
    }
    return (
      <ImageList cols={getImageGridCols()} gap={8}>
        {mediaList.map((media) => (
          <MediaGridItem
            key={media._id}
            media={media}
            likeCount={likeCounts[media._id] || 0} // Pass the global like count for display
            currentDisplaySize={currentDisplaySize} // Pass the image size determined by claims
          />
        ))}
      </ImageList>
    );
  };

  // Helper function to render media organized by divesite
  const renderDivesiteView = () => {
    return Object.keys(mediaByDivesite).length > 0 ? (
      Object.entries(mediaByDivesite).map(([divesiteName, mediaList]) => (
        <Accordion key={divesiteName} defaultExpanded={false}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`divesite-${divesiteName}-content`}
            id={`divesite-${divesiteName}-header`}
          >
            <Typography variant="subtitle2">
              {divesiteName} ({mediaList.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>{renderMediaGrid(mediaList)}</AccordionDetails>
        </Accordion>
      ))
    ) : (
      <Typography color="textSecondary">No divesite media uploaded yet.</Typography>
    );
  };

  // Helper function to render media organized by dive
  const renderDiveView = () => {
    return Object.keys(mediaByDive).length > 0 ? (
      Object.entries(mediaByDive).map(([diveName, mediaList]) => (
        <Accordion key={diveName} defaultExpanded={false}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`dive-${diveName}-content`}
            id={`dive-${diveName}-header`}
          >
            <Typography variant="subtitle2">
              {diveName} ({mediaList.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>{renderMediaGrid(mediaList)}</AccordionDetails>
        </Accordion>
      ))
    ) : (
      <Typography color="textSecondary">No dive media uploaded yet.</Typography>
    );
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
          Your Uploaded Media
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip label={`Total Images: ${totalMediaCount}`} color="primary" size="small" />
          <Chip label={`Divesites: ${divesiteCount}`} color="secondary" size="small" />
          <Chip label={`Dives: ${diveCount}`} color="info" size="small" />
        </Stack>

        {mediaError && ( // Display general media loading errors
          <Alert severity="error" sx={{ mb: 2 }}>
            {mediaError}
          </Alert>
        )}

        {mediaLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }} aria-label="media organization tabs">
              <Tab label="By Divesite" />
              <Tab label="By Dive" />
            </Tabs>

            {tabValue === 0 ? renderDivesiteView() : renderDiveView()}
          </>
        )}
      </CardContent>
    </Card>
  );
}